import { useEffect, useRef, useState } from "react";
import { GetProtocols } from "@/services/fhir/questionnaireresponse/getProtocols";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase, PatientsProgress } from "src-next/modules/clinical-insights/loadPhase";
import { insightAnalysisCacheKey } from "src-next/modules/clinical-insights/helpers/insightAnalysisCacheKey";
import { normalizePatientId, patientDisplayName } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import {
  deriveInsightLoadPhase,
  useCachedInsightPayload,
  useReportInsightLoadPhase,
} from "src-next/modules/clinical-insights/hooks/useCachedInsightPayload";
import { useInsightOrganizationPatients } from "src-next/modules/clinical-insights/hooks/useInsightOrganizationPatients";
import { getOrFetchInsightPatients } from "src-next/modules/clinical-insights/services/insightPatientCache";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";
import { sharedMemoryCache } from "src-next/shared/cache/createMemoryCache";

export interface ProtocolCountRow {
  protocolId: string;
  label: string;
  count: number;
}

interface ProtocolQrPayload {
  entries: Array<{ resource?: fhir5.QuestionnaireResponse }>;
}

function protocolDetailCacheKey(
  author: string,
  practitionerFilter: string,
  protocolId: string,
  startDate?: string,
  endDate?: string,
): string | null {
  return insightAnalysisCacheKey({
    analysisId: `protocolos-qr:${protocolId}`,
    organizationId: author,
    startDate,
    endDate,
    practitionerFilter,
  });
}

async function loadProtocolCounts(
  author: string,
  practitionerFilter: string,
  dateRange?: ClinicalInsightDateRange,
): Promise<ProtocolCountRow[]> {
  const protocolsBundle = await GetProtocols({ author });
  const protocolEntries: { resource?: fhir5.QuestionnaireResponse }[] =
    protocolsBundle?.entry || [];

  const protocolsList = protocolEntries
    .map((entry) => entry?.resource)
    .filter(
      (resource): resource is fhir5.QuestionnaireResponse =>
        !!resource && resource.resourceType === "QuestionnaireResponse",
    )
    .filter((resource) => !!resource.id);

  if (!protocolsList.length) return [];

  const results = await Promise.allSettled(
    protocolsList.map(async (resource) => {
      const id = resource.id as string;
      const summary = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: true,
        protocol: id,
        count_num: true,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });
      const total = Number.isFinite(summary?.total) ? summary.total : 0;
      const nameItem = resource.item?.find((item) => item.linkId === "438586562091");
      const protocolName =
        nameItem?.answer?.[0]?.valueString ||
        resource.title ||
        resource.name ||
        `Protocolo ${id}`;
      const label = typeof protocolName === "string" ? protocolName : `Protocolo ${id}`;
      return { protocolId: id, label, count: total };
    }),
  );

  const fulfilled = results.filter(
    (result): result is PromiseFulfilledResult<ProtocolCountRow> =>
      result.status === "fulfilled",
  );
  if (fulfilled.length === 0) {
    throw new Error("Erro ao buscar protocolos");
  }

  return fulfilled
    .map((result) => result.value)
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);
}

async function getOrFetchProtocolEntries(
  author: string,
  practitionerFilter: string,
  protocolId: string,
  dateRange?: ClinicalInsightDateRange,
): Promise<ProtocolQrPayload> {
  const cacheKey = protocolDetailCacheKey(
    author,
    practitionerFilter,
    protocolId,
    dateRange?.startDate,
    dateRange?.endDate,
  );
  if (!cacheKey) return { entries: [] };

  return sharedMemoryCache.getOrFetch(cacheKey, async () => {
    const bundle = await GetQuestionnaireResponse({
      author,
      practitioner: practitionerFilter,
      hasProtocols: true,
      protocol: protocolId,
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
    });
    return { entries: bundle?.entry || [] };
  });
}

interface Args {
  author: string;
  practitionerFilter: string;
  dateRange?: ClinicalInsightDateRange;
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  fetchEnabled?: boolean;
  isActive?: boolean;
}

export function useProtocolAnalysisData({
  author,
  practitionerFilter,
  dateRange,
  onLoadPhaseChange,
  fetchEnabled = true,
  isActive = true,
}: Args) {
  const { data, isLoading, error, refresh } = useCachedInsightPayload<ProtocolCountRow[]>({
    analysisId: "protocolos",
    organizationId: author,
    dateRange,
    practitionerFilter,
    enabled: fetchEnabled && Boolean(author),
    fetcher: () => loadProtocolCounts(author, practitionerFilter, dateRange),
  });

  const protocols = data ?? [];
  const [patientRows, setPatientRows] = useState<InsightPatientRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [patientsProgress, setPatientsProgress] = useState<PatientsProgress>({
    loaded: 0,
    total: 0,
  });

  const { ready: organizationPatientsReady, patientsById: organizationPatients } =
    useInsightOrganizationPatients();
  const organizationPatientsRef = useRef(organizationPatients);
  organizationPatientsRef.current = organizationPatients;

  useEffect(() => {
    if (!author || !data) {
      setPatientRows([]);
      setDetailLoading(false);
      return;
    }

    const ids = data.map((row) => row.protocolId);
    if (ids.length === 0) {
      setPatientRows([]);
      setDetailLoading(false);
      setPatientsProgress({ loaded: 0, total: 0 });
      return;
    }

    if (!organizationPatientsReady) {
      setDetailLoading(true);
      return;
    }

    const bundlesCached = ids.every((protocolId) => {
      const cacheKey = protocolDetailCacheKey(
        author,
        practitionerFilter,
        protocolId,
        dateRange?.startDate,
        dateRange?.endDate,
      );
      return Boolean(cacheKey && sharedMemoryCache.get(cacheKey));
    });

    let cancelled = false;
    if (!bundlesCached) {
      setDetailLoading(true);
      setPatientsProgress({ loaded: 0, total: ids.length });
    } else {
      setDetailLoading(false);
    }

    void (async () => {
      const rowMap = new Map<string, InsightPatientRow>();
      const patientIds = new Set<string>();

      for (let i = 0; i < ids.length; i++) {
        if (cancelled) return;
        const protocolId = ids[i];
        const label =
          data.find((row) => row.protocolId === protocolId)?.label ??
          `Protocolo ${protocolId}`;

        try {
          const payload = await getOrFetchProtocolEntries(
            author,
            practitionerFilter,
            protocolId,
            dateRange,
          );
          for (const entry of payload.entries) {
            const resource = entry?.resource;
            if (!resource || resource.resourceType !== "QuestionnaireResponse") continue;
            const patientId = normalizePatientId(resource.subject?.reference || "");
            if (!patientId) continue;
            patientIds.add(patientId);

            const date = resource.authored
              ? new Date(resource.authored).toLocaleDateString()
              : "Não informado";
            const pract = resource.author?.display || "Não informado";

            if (!rowMap.has(patientId)) {
              rowMap.set(patientId, {
                patientId,
                patientName: "…",
                items: [],
              });
            }
            rowMap.get(patientId)!.items.push({
              itemKey: protocolId,
              label,
              statusKey: "applied",
              statusLabel: "Aplicado",
              practitioner: pract,
              date,
            });
          }
        } catch (err) {
          console.error("Erro ao buscar pacientes do protocolo:", protocolId, err);
        }

        if (!cancelled) setPatientsProgress({ loaded: i + 1, total: ids.length });
      }

      if (cancelled) return;

      const patients = await getOrFetchInsightPatients(Array.from(patientIds), {
        organizationPatients: organizationPatientsRef.current,
        shouldContinue: () => !cancelled,
        onProgress: (loaded, total) => {
          if (!cancelled) setPatientsProgress({ loaded, total });
        },
      });

      if (cancelled) return;

      const rows = Array.from(rowMap.values()).map((row) => ({
        ...row,
        patientName: patientDisplayName(patients.get(row.patientId)),
        patient: patients.get(row.patientId),
      }));

      setPatientRows(rows.sort((a, b) => b.items.length - a.items.length));
      setDetailLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    author,
    practitionerFilter,
    dateRange?.startDate,
    dateRange?.endDate,
    dateRange,
    data,
    organizationPatientsReady,
  ]);

  const loadPhase = deriveInsightLoadPhase({
    enabled: fetchEnabled && Boolean(author),
    isLoading,
    error,
    hasPayload: data !== undefined,
    patientsLoading: detailLoading,
  });

  useReportInsightLoadPhase(isActive, loadPhase, onLoadPhaseChange);

  const rankingItems: InsightChartItem[] = protocols.map((row) => ({
    key: row.protocolId,
    label: row.label,
    value: row.count,
  }));

  return {
    protocols,
    rankingItems,
    patientRows,
    loadPhase,
    patientsProgress,
    hasData: data !== undefined,
    retry: () => {
      void refresh();
    },
  };
}
